/**
 * ArticlesDetailController — page `articles-detail.html?id={id}` (EPIC-010 / 10.4).
 *
 * Hydrate la fiche d'un article depuis `GET /articles/{id}` puis offre
 * les actions Modifier (lien direct), Archiver (POST direct + toast, ou
 * modale bloquée sur 422 `blocked_by_recurring_invoices`), Restaurer.
 *
 * Convention CLAUDE.md respectée : aucun HTML inline JS, aucune classe
 * inventée. Les libellés/catégories suivent le contrat 10.2 (vatRate
 * `0|2.1|5.5|10|20`, activity.category `bic_vente|bic_services|bnc`).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const CATEGORY_LABELS = {
  bic_vente: "BIC vente",
  bic_services: "BIC services",
  bnc: "BNC",
};

const CATEGORY_PILL_CLASSES = {
  bic_vente: "nm-pill--indigo",
  bic_services: "nm-pill--blue",
  bnc: "nm-pill--olive",
};

const PRICE_FORMATTER = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export default class ArticlesDetailController extends BaseController {
  static targets = [
    "crumb", "title", "statusPill", "statusLabel",
    "categoryPill", "categoryLabel", "meta",
    "editButton", "loadError",
    "archivedActivityBanner",
    "description",
    "resumeReference", "resumeActivity",
    "resumeCategoryPill", "resumeCategoryLabel",
    "resumeUnit", "resumePrice", "resumeVat",
    "archiveButton", "restoreButton",
    "blockedModal", "blockedCount", "blockedRecurringLink",
  ];

  connect() {
    this._parseId();
    if (!this._articleId) {
      this._showLoadError("Identifiant article manquant dans l'URL.");
      return;
    }
    this._activitiesById = new Map();
    this._fetchAll();
  }

  _parseId() {
    // Forme canonique (EPIC-099.53) : articles-detail.html?id={uuid}
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    this._articleId = id || null;
  }

  async _fetchAll() {
    try {
      const [articleResp, activitiesResp] = await Promise.all([
        api.getArticle(this._articleId),
        api.listActivities(),
      ]);
      this._cacheActivities(activitiesResp);
      this._article = articleResp?.data ?? articleResp;
      this._render();
    } catch (error) {
      if (error instanceof APIError && error.status === 404) {
        this._showLoadError("Article introuvable.");
        return;
      }
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger l'article.";
      this._showLoadError(msg);
    }
  }

  _cacheActivities(response) {
    const list = Array.isArray(response?.activities)
      ? response.activities
      : Array.isArray(response?.data) ? response.data : [];
    for (const activity of list) {
      this._activitiesById.set(activity.id, activity);
    }
  }

  _resolveActivity(article) {
    if (article.activity && typeof article.activity === "object") {
      return article.activity;
    }
    if (article.activityId && this._activitiesById.has(article.activityId)) {
      return this._activitiesById.get(article.activityId);
    }
    return null;
  }

  _showLoadError(message) {
    if (this.hasLoadErrorTarget) {
      this.loadErrorTarget.textContent = message;
      this.loadErrorTarget.hidden = false;
    }
    if (this.hasMetaTarget) this.metaTarget.textContent = "Erreur de chargement";
  }

  _render() {
    const article = this._article;
    if (!article) return;

    const label = article.label ?? "—";
    const isArchived = article.status === "archived";
    const activity = this._resolveActivity(article);
    const category = activity?.category ?? null;

    if (this.hasCrumbTarget) this.crumbTarget.textContent = label;
    if (this.hasTitleTarget) this.titleTarget.textContent = label;

    if (this.hasStatusPillTarget) {
      this.statusPillTarget.hidden = false;
      this.statusPillTarget.classList.remove("nm-pill--olive");
      if (!isArchived) this.statusPillTarget.classList.add("nm-pill--olive");
      if (this.hasStatusLabelTarget) {
        this.statusLabelTarget.textContent = isArchived ? "archivé" : "actif";
      }
    }

    if (this.hasCategoryPillTarget) {
      this._applyCategoryPill(this.categoryPillTarget, this.categoryLabelTarget, category);
    }

    if (this.hasMetaTarget) {
      this.metaTarget.textContent = this._formatMeta(article);
    }

    if (this.hasEditButtonTarget) {
      this.editButtonTarget.setAttribute("href", `/articles-edition.html?id=${encodeURIComponent(article.id)}`);
    }

    if (this.hasDescriptionTarget) {
      if (article.description && article.description.trim() !== "") {
        this.descriptionTarget.textContent = article.description;
        this.descriptionTarget.classList.remove("is-muted");
      } else {
        this.descriptionTarget.textContent = "Aucune description renseignée.";
        this.descriptionTarget.classList.add("is-muted");
      }
    }

    if (this.hasResumeReferenceTarget) {
      this.resumeReferenceTarget.textContent = article.reference ?? "—";
    }
    if (this.hasResumeActivityTarget) {
      this.resumeActivityTarget.textContent = activity?.description ?? "—";
    }
    if (this.hasResumeCategoryPillTarget) {
      this._applyCategoryPill(this.resumeCategoryPillTarget, this.resumeCategoryLabelTarget, category);
    }
    if (this.hasResumeUnitTarget) this.resumeUnitTarget.textContent = article.unit ?? "—";
    if (this.hasResumePriceTarget) {
      this.resumePriceTarget.textContent = `${this._formatPrice(article.priceExclVat)} €`;
    }
    if (this.hasResumeVatTarget) this.resumeVatTarget.textContent = this._formatVatHint(article.vatRate);

    if (this.hasArchivedActivityBannerTarget) {
      this.archivedActivityBannerTarget.hidden = !(activity && activity.status === "archived");
    }

    if (this.hasArchiveButtonTarget) this.archiveButtonTarget.hidden = isArchived;
    if (this.hasRestoreButtonTarget) this.restoreButtonTarget.hidden = !isArchived;
  }

  _applyCategoryPill(pill, labelEl, category) {
    Object.values(CATEGORY_PILL_CLASSES).forEach((cls) => pill.classList.remove(cls));
    if (category && CATEGORY_PILL_CLASSES[category]) {
      pill.classList.add(CATEGORY_PILL_CLASSES[category]);
      pill.hidden = false;
      if (labelEl) labelEl.textContent = CATEGORY_LABELS[category] ?? category;
    } else {
      pill.hidden = true;
    }
  }

  _formatMeta(article) {
    if (!article.createdAt) return "";
    const date = new Date(article.createdAt);
    if (Number.isNaN(date.getTime())) return "";
    return `Créé le ${this.formatDate(article.createdAt)}`;
  }

  _formatPrice(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return PRICE_FORMATTER.format(n);
  }

  _formatVatHint(vatRate) {
    // `null` = article créé en franchise (vat_rate IS NULL en base) ;
    // `"0"` = article legacy au taux 0 (avant la nullabilité de §4) —
    // même rendu utilisateur dans les deux cas.
    if (vatRate === null || vatRate === undefined || vatRate === "0" || Number(vatRate) === 0) {
      return "Non applicable — art. 293 B du CGI";
    }
    return `${vatRate} %`;
  }

  // ---------------------------------------------------------------------------
  // Actions archive / restore
  // ---------------------------------------------------------------------------

  async archive(event) {
    event?.preventDefault?.();
    if (!this._articleId) return;
    this.archiveButtonTarget.disabled = true;
    try {
      const response = await api.archiveArticle(this._articleId);
      this._article = response?.data ?? response;
      this._render();
      this.showSuccess("Article archivé.");
    } catch (error) {
      const handled = this._maybeShowBlockedModal(error);
      if (!handled) {
        const msg = error instanceof APIError && error.message
          ? error.message
          : "Erreur lors de l'archivage.";
        this.showError(msg);
      }
    } finally {
      this.archiveButtonTarget.disabled = false;
    }
  }

  async restore(event) {
    event?.preventDefault?.();
    if (!this._articleId) return;
    this.restoreButtonTarget.disabled = true;
    try {
      const response = await api.restoreArticle(this._articleId);
      this._article = response?.data ?? response;
      this._render();
      this.showSuccess("Article restauré.");
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Erreur lors de la restauration.";
      this.showError(msg);
    } finally {
      this.restoreButtonTarget.disabled = false;
    }
  }

  _maybeShowBlockedModal(error) {
    if (!(error instanceof APIError) || error.status !== 422) return false;
    const details = error.data?.error?.details;
    const code = details?.code;
    if (code !== "blocked_by_recurring_invoices") return false;
    const count = Number(details.recurringInvoiceCount) || 0;
    if (this.hasBlockedCountTarget) {
      this.blockedCountTarget.textContent = String(count);
    }
    // Le lien vers la liste des récurrentes reste `hidden` en V1
    // (route récurrentes = EPIC-014).
    if (this.hasBlockedModalTarget && typeof this.blockedModalTarget.showModal === "function") {
      this.blockedModalTarget.showModal();
    }
    return true;
  }

  closeBlockedModal(event) {
    event?.preventDefault?.();
    if (this.hasBlockedModalTarget && typeof this.blockedModalTarget.close === "function") {
      this.blockedModalTarget.close();
    }
  }
}
